import React, { useState } from 'react';
import { Shield, TrendingUp, Calendar, DollarSign, Calculator } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const TesouroDireto: React.FC = () => {
  useSEO({
    title: 'Tesouro Direto 2026: Simulador Tesouro Selic, IPCA+ e Prefixado | O Teu Dinheiro',
    description: 'Guia completo e simulador do Tesouro Direto em 2026. Descubra qual título público escolher entre Tesouro Selic, Tesouro IPCA+ e Renda+.',
    keywords: 'Tesouro Direto 2026, Tesouro Selic, Tesouro IPCA+, simulador Tesouro Direto, quanto rende o Tesouro Direto',
    url: 'https://www.oteudinheiro.com.br/tesouro-direto'
  });
  const [valor, setValor] = useState('');
  const [prazo, setPrazo] = useState('');
  const [tipo, setTipo] = useState('selic');

  const calcularRendimento = () => {
    if (!valor || !prazo) return null;

    const valorNum = parseFloat(valor);
    const prazoNum = parseInt(prazo);

    if (tipo === 'selic') {
      // Taxa Selic atual (aproximada)
      const taxaAnual = 0.1175; // 11.75% ao ano
      const rendimento = valorNum * Math.pow(1 + taxaAnual, prazoNum / 12) - valorNum;
      return rendimento.toFixed(2);
    } else if (tipo === 'ipca') {
      // IPCA + 5.5% ao ano (aproximado)
      const taxaAnual = 0.055; // 5.5% ao ano
      const rendimento = valorNum * Math.pow(1 + taxaAnual, prazoNum / 12) - valorNum;
      return rendimento.toFixed(2);
    }

    return null;
  };

  const rendimento = calcularRendimento();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-700 text-white py-20">
        <div className="container-custom text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Tesouro Direto
          </h1>
          <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
            O investimento mais seguro do Brasil: títulos do governo federal
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que é o Tesouro Direto?
              </h2>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  O Tesouro Direto é um programa do governo federal que permite que pessoas físicas
                  comprem títulos públicos diretamente, sem precisar de um banco ou corretora.
                </p>
                <p className="mb-4">
                  É considerado o investimento mais seguro do Brasil, pois é garantido pelo governo federal.
                  Se o governo não pagar, você recebe o dinheiro de volta.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Principais tipos de títulos
              </h3>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                    Tesouro Selic
                  </h4>
                  <p className="text-gray-600 mb-3">
                    Rende a taxa Selic (atualmente 15% ao ano). Ideal para reserva de emergência.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Liquidez diária</li>
                    <li>• Sem risco de perda</li>
                    <li>• Ideal para iniciantes</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                    Tesouro IPCA
                  </h4>
                  <p className="text-gray-600 mb-3">
                    Rende IPCA + taxa fixa. Protege contra inflação e rende mais que a Selic.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Proteção contra inflação</li>
                    <li>• Maior rentabilidade</li>
                    <li>• Prazo definido</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Vantagens do Tesouro Direto
              </h3>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Segurança máxima</h4>
                    <p className="text-gray-600 text-sm">Garantia do governo federal</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Baixo investimento</h4>
                    <p className="text-gray-600 text-sm">A partir de R$ 30,00</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Liquidez</h4>
                    <p className="text-gray-600 text-sm">Resgaste em até 1 dia útil</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Calculator className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Simplicidade</h4>
                    <p className="text-gray-600 text-sm">Fácil de entender e usar</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Simulador de Rendimento
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor a investir (R$)
                    </label>
                    <input
                      type="number"
                      value={valor}
                      onChange={(e) => setValor(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prazo (meses)
                    </label>
                    <input
                      type="number"
                      value={prazo}
                      onChange={(e) => setPrazo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de título
                    </label>
                    <select
                      value={tipo}
                      onChange={(e) => setTipo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="selic">Tesouro Selic</option>
                      <option value="ipca">Tesouro IPCA</option>
                    </select>
                  </div>

                  {rendimento && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-center">
                        <p className="text-sm text-green-600 mb-1">Rendimento estimado:</p>
                        <p className="text-2xl font-bold text-green-700">
                          R$ {rendimento}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          * Valores aproximados, sujeitos a variações
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Invest Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como investir no Tesouro Direto
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Abra uma conta
              </h3>
              <p className="text-gray-600">
                Crie sua conta no site do Tesouro Direto ou use uma corretora.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Escolha o título
              </h3>
              <p className="text-gray-600">
                Selecione entre Selic, IPCA ou outros títulos disponíveis.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Defina o valor
              </h3>
              <p className="text-gray-600">
                Escolha quanto quer investir (mínimo R$ 30,00).
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Confirme e pague
              </h3>
              <p className="text-gray-600">
                Confirme os dados e faça o pagamento via PIX ou boleto.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas importantes
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Comece pequeno
              </h3>
              <p className="text-secondary-700">
                Comece com valores menores para se familiarizar com o sistema.
                Você pode investir a partir de R$ 30,00.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Atenção aos prazos
              </h3>
              <p className="text-accent-700">
                Alguns títulos têm prazo de vencimento. Resgatar antes pode
                resultar em perdas. Leia sempre o prospecto.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Diversifique
              </h3>
              <p className="text-secondary-700">
                Não coloque todo seu dinheiro em um só tipo de título.
                Misture Selic e IPCA para equilibrar risco e retorno.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Impostos
              </h3>
              <p className="text-accent-700">
                Tesouro Direto paga IOF e IR. Para prazos longos,
                a alíquota de IR pode chegar a 15%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-green-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Comece a investir no Tesouro Direto hoje!
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            É o investimento mais seguro do Brasil e ideal para quem está começando.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.tesourodireto.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white text-green-600 hover:bg-gray-100"
            >
              Acessar Tesouro Direto
            </a>
            <a
              href="/investimentos"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-green-600"
            >
              Ver outros investimentos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TesouroDireto;
